/* eslint-disable */
/**
 * Session 60.23 · EventDetail
 *
 * Canonical destination for event cards, notifications, reminders, email
 * links, social shares, calendar links, and Commons associations.
 * Route: /events/local/:slug
 *
 * Slug OR event_id resolves server-side via GET /api/events/local/:key
 * (the backend accepts either).
 *
 * External events: Métier RSVP records member INTENT, backend returns
 * `registration_url` + `external_registration_pending`. UI shows
 * disclaimer + "Continue to Registration" CTA per CTO copy spec (answer #2).
 * The Métier RSVP is never presented as a ticket.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";

function fmtRange(startIso, endIso) {
  if (!startIso) return null;
  try {
    const s = new Date(startIso);
    const opts = { weekday: "long", month: "long", day: "numeric" };
    const timeOpts = { hour: "numeric", minute: "2-digit" };
    const day = s.toLocaleDateString(undefined, opts);
    const startT = s.toLocaleTimeString(undefined, timeOpts);
    if (!endIso) return `${day} · ${startT}`;
    const e = new Date(endIso);
    const sameDay = s.toDateString() === e.toDateString();
    if (sameDay) return `${day} · ${startT} – ${e.toLocaleTimeString(undefined, timeOpts)}`;
    return `${day} · ${startT} → ${e.toLocaleDateString(undefined, opts)} · ${e.toLocaleTimeString(undefined, timeOpts)}`;
  } catch { return null; }
}

function classBadge(cls) {
  const map = {
    metier_presents: { label: "Métier Presents", color: "#B8913A" },
    partner: { label: "Partner Event", color: "#3A6BB8" },
    aggregated: { label: "Discovered Event", color: "rgba(0,0,0,0.5)" },
  };
  return map[cls] || map.aggregated;
}

function costLabel(c) {
  return { free: "Free", paid: "Paid", unknown: "See organizer" }[c] || "See organizer";
}

function modalityLabel(m) {
  return { in_person: "In person", virtual: "Virtual", hybrid: "Hybrid" }[m] || "In person";
}

// Session 60.23 · anon session id for view uniqueness. Persisted so a
// reload of the same detail page counts as the same viewer.
function getAnonSessionId() {
  try {
    let s = localStorage.getItem("metier_anon_session_id");
    if (!s) {
      s = `anon_${crypto.randomUUID ? crypto.randomUUID().slice(0, 12) : Math.random().toString(36).slice(2, 14)}`;
      localStorage.setItem("metier_anon_session_id", s);
    }
    return s;
  } catch { return "anon_fallback"; }
}

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpState, setRsvpState] = useState({ going: false, external_registration_pending: false });
  const viewFiredRef = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/events/local/${slug}`)
      .then(r => setEv(r.data || null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(load, [load]);

  // Fire the "view" analytics ping exactly once per page-load.
  useEffect(() => {
    if (!ev?.event_id || viewFiredRef.current) return;
    viewFiredRef.current = true;
    api.post(`/events/${ev.event_id}/analytics`, {
      event_type: "view",
      session_id: getAnonSessionId(),
      utm_source: new URLSearchParams(window.location.search).get("utm_source") || null,
      utm_medium: new URLSearchParams(window.location.search).get("utm_medium") || null,
      utm_campaign: new URLSearchParams(window.location.search).get("utm_campaign") || null,
    }).catch(() => {});
  }, [ev?.event_id]);

  // Hydrate current RSVP state for the logged-in user.
  useEffect(() => {
    if (!user || !ev?.event_id) return;
    // Lightweight probe: attempting RSVP endpoint would toggle; instead we
    // read the user's RSVPs list if that endpoint exists, else we treat as
    // unknown and let the button click set truth.
    api.get(`/me/rsvps`).then(r => {
      const mine = (r.data || []).find(x => x.event_id === ev.event_id);
      if (mine && (mine.status || "going") === "going") {
        setRsvpState({ going: true, external_registration_pending: ev.registration_type === "external" });
      }
    }).catch(() => { /* endpoint may not exist yet — non-fatal */ });
  }, [user && user.user_id, ev?.event_id]);

  const range = useMemo(() => fmtRange(ev?.start_at, ev?.end_at), [ev?.start_at, ev?.end_at]);
  const badge = classBadge(ev?.classification);
  // Session 60.27 · Detect a past/completed event so we can suppress the
  // active RSVP + Continue-to-Registration CTAs. Uses end_at when present,
  // otherwise start_at + 24h — same rule the backend `_event_is_upcoming`
  // helper applies to decide sweep + section placement.
  const isPast = useMemo(() => {
    if (!ev) return false;
    const nowMs = Date.now();
    const endMs = ev.end_at ? Date.parse(ev.end_at) : NaN;
    if (Number.isFinite(endMs)) return endMs < nowMs;
    const startMs = ev.start_at ? Date.parse(ev.start_at) : NaN;
    if (Number.isFinite(startMs)) return startMs + 24 * 60 * 60 * 1000 < nowMs;
    return false;
  }, [ev]);

  const handleRsvp = useCallback(async () => {
    if (!ev?.event_id) return;
    if (!user) { navigate("/login"); return; }
    try {
      const r = await api.post(`/events/${ev.event_id}/rsvp`);
      const going = !!r.data?.going;
      setRsvpState({
        going,
        external_registration_pending: !!r.data?.external_registration_pending,
      });
      if (going) {
        if (r.data?.registration_type === "external") {
          toast.success("RSVP’d on Métier — organizer registration still required.");
        } else {
          toast.success("You’re RSVP’d.");
        }
      } else {
        toast.message("RSVP canceled.");
      }
    } catch (e) {
      toast.error("Couldn’t update your RSVP — try again in a moment.");
    }
  }, [ev?.event_id, ev?.registration_type, user, navigate]);

  const handleExternal = useCallback(() => {
    if (!ev?.event_id || !ev?.registration_url) return;
    api.post(`/events/${ev.event_id}/analytics`, {
      event_type: "external_click",
      session_id: getAnonSessionId(),
    }).catch(() => {});
    window.open(ev.registration_url, "_blank", "noopener,noreferrer");
  }, [ev?.event_id, ev?.registration_url]);

  const handleShare = useCallback(async () => {
    if (!ev?.event_id) return;
    const shareUrl = `${window.location.origin}/events/local/${ev.slug || ev.event_id}`;
    api.post(`/events/${ev.event_id}/analytics`, {
      event_type: "share_started",
      session_id: getAnonSessionId(),
      metadata: { method: navigator.share ? "web_share_api" : "copy_link" },
    }).catch(() => {});
    if (navigator.share) {
      try { await navigator.share({ title: ev.title, url: shareUrl }); return; } catch { /* fallthrough */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch { toast.message(shareUrl); }
  }, [ev]);

  const handleCalendarAdd = useCallback(() => {
    if (!ev?.event_id) return;
    api.post(`/events/${ev.event_id}/analytics`, {
      event_type: "calendar_add",
      session_id: getAnonSessionId(),
    }).catch(() => {});
    // Direct .ics download works for Apple Calendar + Outlook + import into
    // Google Calendar. We add a separate Google-Calendar template link too.
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${backend}/api/events/local/${ev.slug || ev.event_id}/calendar.ics`;
  }, [ev]);

  const googleCalendarUrl = useMemo(() => {
    if (!ev?.start_at) return null;
    const toG = (iso) => {
      try {
        const d = new Date(iso);
        return d.toISOString().replace(/[-:]|\.\d+/g, "");
      } catch { return null; }
    };
    const s = toG(ev.start_at); const e = toG(ev.end_at) || s;
    if (!s) return null;
    const details = encodeURIComponent((ev.description || "").slice(0, 800));
    const loc = encodeURIComponent([ev.venue, ev.city].filter(Boolean).join(", "));
    const text = encodeURIComponent(ev.title || "Métier event");
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${s}/${e}&details=${details}&location=${loc}`;
  }, [ev]);

  const handleDiscussInCommons = useCallback(() => {
    if (!ev?.event_id) return;
    navigate(`/community?event_id=${ev.event_id}`);
  }, [ev?.event_id, navigate]);

  if (loading) return (
    <AppShell>
      <div className="m-page-pad" data-testid="event-detail-loading">
        <p className="m-muted">Loading event…</p>
      </div>
    </AppShell>
  );
  if (!ev) return (
    <AppShell>
      <div className="m-page-pad" data-testid="event-detail-notfound">
        <h1 className="m-h1">Event <em>not found</em>.</h1>
        <p className="m-muted">This event may have ended or been canceled.</p>
        <Link to="/events" className="m-btn-ghost" data-testid="event-detail-back">← Back to events</Link>
      </div>
    </AppShell>
  );

  const isExternal = ev.registration_type === "external";

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="m-page-pad" data-testid="event-detail-page">
        <Link to="/events" className="m-muted" style={{ fontSize: 13, textDecoration: "none" }}
              data-testid="event-detail-back">← All events</Link>

        {ev.image_url && (
          <div style={{ marginTop: 14, borderRadius: 12, overflow: "hidden", maxHeight: 420 }}>
            <img src={ev.image_url} alt={ev.title} style={{ width: "100%", display: "block" }} loading="lazy" />
          </div>
        )}

        <div style={{ marginTop: 18, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span data-testid="event-classification-badge"
                style={{
                  fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                  padding: "4px 10px", borderRadius: 999, fontWeight: 700,
                  color: badge.color, border: `1px solid ${badge.color}`,
                }}>{badge.label}</span>
          {ev.category_label && (
            <span style={{ fontSize: 12, opacity: 0.65 }}>{ev.category_label}</span>
          )}
          <span style={{ fontSize: 12, opacity: 0.65 }}>· {costLabel(ev.cost)}</span>
          <span style={{ fontSize: 12, opacity: 0.65 }}>· {modalityLabel(ev.modality)}</span>
        </div>

        <h1 className="m-h1" style={{ marginTop: 8 }} data-testid="event-detail-title">{ev.title}</h1>
        {ev.organizer && (
          <p className="m-muted" data-testid="event-detail-organizer">by {ev.organizer}</p>
        )}

        <div className="m-card" style={{ marginTop: 18 }}>
          {range && (
            <div style={{ fontWeight: 600 }} data-testid="event-detail-range">{range}</div>
          )}
          {(ev.venue || ev.city) && (
            <div style={{ marginTop: 4, opacity: 0.85 }} data-testid="event-detail-location">
              {ev.venue}{ev.venue && ev.city ? ` · ${ev.city}` : ev.city || ""}
            </div>
          )}
          {ev.capacity ? (
            <div style={{ marginTop: 4, fontSize: 13, opacity: 0.75 }}>
              Capacity {ev.capacity}
            </div>
          ) : null}

          {isExternal && (
            <div
              data-testid="event-external-disclaimer"
              style={{
                marginTop: 14, padding: "10px 12px", borderRadius: 8,
                background: "rgba(184,145,58,0.10)",
                border: "1px solid rgba(184,145,58,0.28)",
                fontSize: 13, lineHeight: 1.55,
              }}>
              Your Métier RSVP does not replace the organizer&#39;s required registration.
              Complete your registration with {ev.organizer || "the organizer"} to secure your spot.
            </div>
          )}

          <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {isPast ? (
              <div
                data-testid="event-past-notice"
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: "rgba(27,35,64,0.08)",
                  border: "1px solid rgba(27,35,64,0.14)",
                  color: "rgba(27,35,64,0.78)",
                  fontSize: 13.5,
                  fontWeight: 500,
                }}
              >
                This event has ended. RSVP and registration are closed.
              </div>
            ) : (
              <button
                type="button"
                className="m-btn-gold"
                data-testid="event-rsvp-button"
                onClick={handleRsvp}
                style={{ padding: "10px 18px" }}
              >
                {rsvpState.going
                  ? (isExternal ? "RSVP’d — Cancel Métier RSVP" : "You’re going — Cancel RSVP")
                  : (isExternal ? "Interested / RSVP on Métier" : "RSVP on Métier")}
              </button>
            )}
            {!isPast && isExternal && (
              <button
                type="button"
                className="m-btn-ghost"
                data-testid="event-external-continue"
                onClick={handleExternal}
                disabled={!ev.registration_url}
                style={{ padding: "10px 18px" }}
              >
                Continue to Registration ↗
              </button>
            )}
            <button
              type="button"
              className="m-btn-ghost"
              data-testid="event-share-button"
              onClick={handleShare}
              style={{ padding: "10px 16px" }}
            >
              Share
            </button>
            <button
              type="button"
              className="m-btn-ghost"
              data-testid="event-calendar-ics"
              onClick={handleCalendarAdd}
              style={{ padding: "10px 16px" }}
            >
              Add to Calendar (.ics)
            </button>
            {googleCalendarUrl && (
              <a
                href={googleCalendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-calendar-google"
                className="m-btn-ghost"
                style={{ padding: "10px 16px", textDecoration: "none" }}
                onClick={() => {
                  api.post(`/events/${ev.event_id}/analytics`, {
                    event_type: "calendar_add",
                    session_id: getAnonSessionId(),
                    metadata: { provider: "google" },
                  }).catch(() => {});
                }}
              >
                Add to Google Calendar
              </a>
            )}
          </div>
        </div>

        {ev.description && (
          <div style={{ marginTop: 18 }} data-testid="event-detail-description">
            <h2 style={{ fontSize: 15, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.75, marginBottom: 6 }}>About</h2>
            <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}>{ev.description}</p>
          </div>
        )}

        <div style={{ marginTop: 22, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className="m-btn-ghost"
            data-testid="event-commons-discuss"
            onClick={handleDiscussInCommons}
            style={{ padding: "10px 16px" }}
          >
            Discuss this event in Métier Commons →
          </button>
        </div>
      </div>
    </AppShell>
  );
}
